import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  ExternalLink,
  Eye,
  EyeOff
} from 'lucide-react';
import { runOAuthDiagnostics, type OAuthDiagnostic } from '@/utils/oauthDiagnostics';
import { useAuth } from '@/contexts/AuthContext';
import { useOAuthFlow } from '@/hooks/useOAuthFlow';
import { toast } from 'sonner';

export const OAuthTester = () => {
  const { user } = useAuth();
  const [diagnostics, setDiagnostics] = useState<OAuthDiagnostic[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { connectPlatform, isConnecting } = useOAuthFlow();

  const runDiagnostics = async () => {
    if (!user) {
      toast.error('Please sign in to run diagnostics');
      return;
    }

    setLoading(true);
    try {
      const results = await runOAuthDiagnostics();
      setDiagnostics(results);
    } catch (error) {
      console.error('Diagnostics failed:', error);
      toast.error('Failed to run diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (platform: string) => {
    if (!user) {
      toast.error('Please sign in to test connections');
      return;
    }

    try {
      await connectPlatform(platform as any);
    } catch (error) {
      console.error(`Failed to test ${platform}:`, error);
    }
  };

  const getStatusIcon = (status: OAuthDiagnostic['status']) => {
    switch (status) {
      case 'working':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'not_configured':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: OAuthDiagnostic['status']) => {
    switch (status) {
      case 'working':
        return <Badge className="bg-green-100 text-green-800">Working</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      case 'not_configured':
        return <Badge variant="secondary">Not Configured</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const platformDisplayNames = {
    twitter: 'Twitter/X',
    facebook: 'Facebook',
    instagram: 'Instagram', 
    reddit: 'Reddit',
    tiktok: 'TikTok',
    threads: 'Threads',
    google_reviews: 'Google Reviews',
    yelp_reviews: 'Yelp Reviews'
  };

  const summary = {
    total: diagnostics.length,
    working: diagnostics.filter(d => d.status === 'working').length,
    errors: diagnostics.filter(d => d.status === 'error').length,
    notConfigured: diagnostics.filter(d => d.status === 'not_configured').length
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            OAuth Integration Tester
            <Button
              onClick={runDiagnostics}
              disabled={loading}
              variant="outline"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Run Diagnostics
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This tool tests OAuth configurations for all supported platforms. 
              Run diagnostics to check which integrations are working correctly.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {diagnostics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Diagnostics Summary
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{summary.total}</div>
                <div className="text-sm text-blue-600">Total Platforms</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{summary.working}</div>
                <div className="text-sm text-green-600">Working</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{summary.errors}</div>
                <div className="text-sm text-red-600">Errors</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{summary.notConfigured}</div>
                <div className="text-sm text-yellow-600">Not Configured</div>
              </div>
            </div>

            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4">
                <div className="grid gap-4">
                  {diagnostics.map((diagnostic) => (
                    <div
                      key={diagnostic.platform}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(diagnostic.status)}
                        <div>
                          <h3 className="font-medium">
                            {platformDisplayNames[diagnostic.platform as keyof typeof platformDisplayNames] || diagnostic.platform}
                          </h3>
                          <p className="text-sm text-gray-600">{diagnostic.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(diagnostic.status)}
                        {diagnostic.status === 'working' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => testConnection(diagnostic.platform)}
                            disabled={isConnecting[diagnostic.platform]}
                          >
                            {isConnecting[diagnostic.platform] ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <ExternalLink className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="details" className="space-y-4">
                {showDetails && (
                  <div className="space-y-4">
                    {diagnostics.map((diagnostic) => (
                      <Card key={diagnostic.platform}>
                        <CardHeader>
                          <CardTitle className="flex items-center space-x-2">
                            {getStatusIcon(diagnostic.status)}
                            <span>
                              {platformDisplayNames[diagnostic.platform as keyof typeof platformDisplayNames] || diagnostic.platform}
                            </span>
                            {getStatusBadge(diagnostic.status)}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div>
                              <strong>Status:</strong> {diagnostic.status}
                            </div>
                            <div>
                              <strong>Message:</strong> {diagnostic.message}
                            </div>
                            {diagnostic.redirectUri && (
                              <div>
                                <strong>Redirect URI:</strong> 
                                <code className="ml-2 px-2 py-1 bg-gray-100 rounded text-sm">
                                  {diagnostic.redirectUri}
                                </code>
                              </div>
                            )}
                            {diagnostic.error && (
                              <div>
                                <strong>Error:</strong> 
                                <code className="ml-2 px-2 py-1 bg-red-100 rounded text-sm text-red-700">
                                  {diagnostic.error}
                                </code>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Required Redirect URIs</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              Ensure these redirect URIs are configured in your OAuth provider settings:
            </AlertDescription>
          </Alert>
          <div className="mt-4 space-y-2">
            <div className="p-3 bg-gray-50 rounded">
              <strong>Production:</strong>
              <ul className="mt-2 space-y-1 text-sm">
                <li><code>https://socialscoop.ai/api/oauth-callback</code></li>
                <li><code>https://thedigitalfence.com/api/oauth-callback</code></li>
              </ul>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <strong>Development:</strong>
              <ul className="mt-2 space-y-1 text-sm">
                <li><code>https://ifkrklrmsjszdloohxvx.supabase.co/functions/v1/oauth-callback</code></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};